import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import { VENDOR_TYPES } from 'right-revenue-microservice-sdk/lib/constants';

import dayjs from 'utils/dayjs';
import { Col, Row } from 'antd';

import {
    getOTAInsightsByRange,
    getOTAInsightsExport,
    getEventsForRange,
} from '../../actions/dashBoardActions';
import session from 'containers/App/session';
import forecastModule from 'containers/App/data/endpoints/forecastByDay';
import statisticsByDayModule from 'containers/App/data/endpoints/statisticsByDay';
import currentStatsModule from 'containers/App/data/endpoints/currentRoomsRevenueByDay';
import lyFinalStatsModule from 'containers/App/data/endpoints/lyFinalRoomsRevenueByDay';
import lySpitStatsModule from 'containers/App/data/endpoints/lySpitRoomsRevenueByDay';
import y2FinalStatsModule from 'containers/App/data/endpoints/y2FinalRoomsRevenueByDay';
import y2SpitStatsModule from 'containers/App/data/endpoints/y2SpitRoomsRevenueByDay';
import budgetByDayModule from 'containers/App/data/endpoints/budgetByDay';
import sellingRateModule from 'containers/App/data/endpoints/sellingRate';
import roomTypesModule from 'containers/App/data/endpoints/roomTypes';
import inventoryBlocksMonthlyModule from 'containers/App/data/endpoints/inventoryBlocksMonthly';

import { getOtaData } from './service';
import { getLYSpitDateFor } from '../../helpers/dateHelper';

import Widget from '../UI/widget';
import Spinner from '../UI/spinner';
import YearMonthSelection from '../UI/yearMonthSelection';
import BusinessReview from './businessReview/businessReview';
import OTAInsights from './otaInsights';
import StatusOverview from '../common/roomsAndRevenueOverview/statusOverview';

import BusinessAnalysis from 'containers/widgets/BusinessAnalysis';
import BusinessStatus from 'containers/widgets/BusinessStatus';
import BusinessChange from 'containers/widgets/BusinessChange';
import InventoryBlocks from 'containers/widgets/InventoryBlocks';
import RecommendationsCalendar from 'containers/widgets/RecommendationsCalendar';

import './dashboard.style.css';
import { FEATURES } from 'utils/permissions';
import NotesManagement from 'containers/shared/NotesManagement/NotesManagement';
import EventsManagement from 'containers/shared/EventsManagement/EventsManagement';

const Dashboard = () => {
    const apiDateFormat = 'YYYY-MM-DD';
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [period, setPeriod] = useState({
        startDate: dayjs().startOf('month').format(apiDateFormat),
        endDate: dayjs().endOf('month').format(apiDateFormat),
        details: {
            year: dayjs().year(),
            type: 'custom',
        },
    });
    const [dayComparison, setDayComparison] = useState(true);
    const accessibleFeatures = useSelector(session.selectors.selectAccessibleFeatures);
    const groupBlocksConfig = useSelector(session.selectors.selectGroupBlocksConfig);
    const propertyId = useSelector(session.selectors.selectSelectedPropertyId);
    const currency = useSelector(session.selectors.selectCurrency);
    const otaCompsets = useSelector(session.selectors.selectOtaCompsets);
    const otaHotelId = useSelector(session.selectors.selectOtaHotelId);
    const selectedPropertyVendorId = useSelector(session.selectors.selectVendorId);

    const groupBlockNonGuaranteedStatuses = get(groupBlocksConfig, 'nonGuaranteedStatuses', undefined);

    const canViewInventoryBlocks = accessibleFeatures.includes(FEATURES.inventoryBlocks);
    const canViewRoomForecast = accessibleFeatures.includes(FEATURES.roomForecast);
    const canViewRevenueForecast = accessibleFeatures.includes(FEATURES.revenueForecast);
    const canViewCardinalRecommendations = accessibleFeatures.includes(FEATURES.cardinalRecommendations);
    const canViewAERecs = accessibleFeatures.includes(FEATURES.viewAERecs);

    const canViewOTAInsights = accessibleFeatures.includes(FEATURES.otaInsights);
    const canViewOTACompsets = accessibleFeatures.includes(FEATURES.otaCompsets);
    const canViewNotes = accessibleFeatures.includes(FEATURES.notes);
    const canViewEvents = accessibleFeatures.includes(FEATURES.events);

    const forecast = useSelector(forecastModule.selectors.selectState);
    const statistics = useSelector(statisticsByDayModule.selectors.selectState);
    const sellingRate = useSelector(sellingRateModule.selectors.selectState);
    const roomTypes = useSelector(roomTypesModule.selectors.selectState);
    const currentStats = useSelector(currentStatsModule.selectors.selectState);
    const lyFinalStats = useSelector(lyFinalStatsModule.selectors.selectState);
    const lySpitStats = useSelector(lySpitStatsModule.selectors.selectState);
    const y2FinalStats = useSelector(y2FinalStatsModule.selectors.selectState);
    const y2SpitStats = useSelector(y2SpitStatsModule.selectors.selectState);
    const budget = useSelector(budgetByDayModule.selectors.selectState);
    const inventoryBlocks = useSelector(inventoryBlocksMonthlyModule.selectors.selectState);

    const isDashboardApiLoading = false;

    const otaState = useSelector((state) => state.dashBoard?.otaInsights);
    const otaInsights = otaState?.payload ? getOtaData(otaState?.payload) : [];

    const eventsState = useSelector((state) => state.dashBoard?.events);
    const events = eventsState?.payload || [];

    const getMonthBoundaries = () => {
        let spitUnit = 'weeks';
        let lySpitAmount = 52;
        let y2SpitAmount = 104;
        if (!dayComparison) {
            spitUnit = 'years';
            lySpitAmount = 1;
            y2SpitAmount = 2;
        }
        const startDate = dayjs(period.startDate);
        const endDate = dayjs(period.endDate);
        return {
            startDate,
            endDate,
            lySpitStartDate: startDate.clone().subtract(lySpitAmount, spitUnit),
            lySpitEndDate: endDate.clone().subtract(lySpitAmount, spitUnit),
            y2SpitStartDate: startDate.clone().subtract(y2SpitAmount, spitUnit),
            y2SpitEndDate: endDate.clone().subtract(y2SpitAmount, spitUnit),
            y2SpitToday: dayjs().subtract(104, 'weeks').endOf('day'),
        };
    };

    const isPeriodInThePast = () => {
        const { endDate } = getMonthBoundaries();
        return dayjs().isAfter(endDate, 'day');
    };

    const fetchEvents = () => {
        const { startDate, endDate } = getMonthBoundaries();
        const start = startDate.format(apiDateFormat);
        const end = endDate.format(apiDateFormat);
        dispatch(getEventsForRange(propertyId, start, end));
    };

    const fetchExport = () => {
        const { startDate, endDate } = getMonthBoundaries();
        const start = startDate.format(apiDateFormat);
        const end = endDate.format(apiDateFormat);
        dispatch(getOTAInsightsExport(propertyId, start, end));
    };

    const onClickDayHandler = (day) => {
        navigate('/app/daily', {
            state: { selectedDate: day.format('YYYY-MM-DD') },
        });
    };

    const onChangePeriod = (periodDetails) => {
        if (periodDetails) {
            setPeriod(periodDetails);
        }
    };

    const getLyData = () => {
        dispatch(lyFinalStatsModule.actions.resetState());
        dispatch(lySpitStatsModule.actions.resetState());
        dispatch(y2FinalStatsModule.actions.resetState());
        dispatch(y2SpitStatsModule.actions.resetState());

        const { lySpitStartDate, lySpitEndDate, y2SpitStartDate, y2SpitEndDate, y2SpitToday } =
            getMonthBoundaries();

        const lySpitStart = lySpitStartDate.format(apiDateFormat);
        const lySpitEnd = lySpitEndDate.format(apiDateFormat);
        const y2SpitStart = y2SpitStartDate.format(apiDateFormat);
        const y2SpitEnd = y2SpitEndDate.format(apiDateFormat);
        const y2SpitNow = y2SpitToday.format(apiDateFormat);

        dispatch(lyFinalStatsModule.actions.fetch(propertyId, lySpitStart, lySpitEnd));
        dispatch(lySpitStatsModule.actions.fetch(propertyId, lySpitStart, lySpitEnd));
        dispatch(y2FinalStatsModule.actions.fetch(propertyId, y2SpitStart, y2SpitEnd));
        dispatch(y2SpitStatsModule.actions.fetch(propertyId, y2SpitStart, y2SpitEnd, y2SpitNow));
    };

    const getApiData = () => {
        dispatch(roomTypesModule.actions.resetState());
        dispatch(currentStatsModule.actions.resetState());
        dispatch(budgetByDayModule.actions.resetState());
        dispatch(forecastModule.actions.resetState());
        dispatch(sellingRateModule.actions.resetState());
        dispatch(statisticsByDayModule.actions.resetState());
        dispatch(forecastModule.actions.resetState());
        dispatch(inventoryBlocksMonthlyModule.actions.resetState());

        const { startDate, endDate } =
            getMonthBoundaries();

        const start = startDate.format(apiDateFormat);
        const end = endDate.format(apiDateFormat);

        dispatch(roomTypesModule.actions.fetch(propertyId));
        dispatch(currentStatsModule.actions.fetch(propertyId, start, end));
        dispatch(budgetByDayModule.actions.fetch(propertyId, start, end));

        getLyData();

        dispatch(getOTAInsightsByRange(propertyId, start, end));

        if (!isPeriodInThePast()) {
            dispatch(
                forecastModule.actions.fetch(
                    propertyId,
                    startDate.isBefore(dayjs(), 'day') ? dayjs().format(apiDateFormat) : start,
                    end,
                ),
            );
            if (canViewInventoryBlocks) {
                dispatch(
                    inventoryBlocksMonthlyModule.actions.fetch(
                        propertyId,
                        startDate.isBefore(dayjs(), 'day') ? dayjs().format(apiDateFormat) : start,
                        end,
                        groupBlockNonGuaranteedStatuses !== undefined
                            ? JSON.stringify(groupBlockNonGuaranteedStatuses)
                            : null,
                    ),
                );
            }
        } else {
            dispatch(forecastModule.actions.resetState());
        }

        if (canViewCardinalRecommendations || canViewAERecs) {
            dispatch(sellingRateModule.actions.fetch(propertyId, start, end));
        }

        dispatch(statisticsByDayModule.actions.fetch(propertyId, start, end));

        fetchEvents();

        fetchExport();
    };

    const onDayOrDateComparisonChange = (dayComparisonValue) => {
        setDayComparison(dayComparisonValue);
    };

    useEffect(() => {
        getApiData();
        return () => {
            dispatch(roomTypesModule.actions.resetState());
            dispatch(currentStatsModule.actions.resetState());
            dispatch(lyFinalStatsModule.actions.resetState());
            dispatch(lySpitStatsModule.actions.resetState());
            dispatch(y2FinalStatsModule.actions.resetState());
            dispatch(y2SpitStatsModule.actions.resetState());
            dispatch(budgetByDayModule.actions.resetState());
            dispatch(forecastModule.actions.resetState());
            dispatch(sellingRateModule.actions.resetState());
            dispatch(statisticsByDayModule.actions.resetState());
            dispatch(forecastModule.actions.resetState());
            dispatch(inventoryBlocksMonthlyModule.actions.resetState());
        };
    }, [propertyId, period.startDate, period.endDate, dayComparison]);

    useEffect(() => {
        getLyData();
        return () => {
            dispatch(lyFinalStatsModule.actions.resetState());
            dispatch(lySpitStatsModule.actions.resetState());
            dispatch(y2FinalStatsModule.actions.resetState());
            dispatch(y2SpitStatsModule.actions.resetState());
        };
    }, [dayComparison]);

    const { isOtaInsightsLoading } = isDashboardApiLoading;

    const { startDate, endDate } = getMonthBoundaries();
    const datesRange = {
        startDate: startDate.format(apiDateFormat),
        endDate: endDate.format(apiDateFormat),
        lySpitStartDate: getLYSpitDateFor(startDate, dayComparison).format(apiDateFormat),
        lySpitEndDate: getLYSpitDateFor(endDate, dayComparison).format(apiDateFormat),
    };

    const isRoomRevenueChangesLoading =
        currentStats.fetching || lyFinalStats.fetching || lySpitStats.fetching || forecast.fetching || budget.fetching;

    return (
        <>
            <YearMonthSelection period={period} dateChanged={onChangePeriod} onDayOrDateComparisonChange={onDayOrDateComparisonChange} dayComparisonValue={dayComparison} />
            <Row>
                <Col span={22} offset={1}>
                    {(canViewCardinalRecommendations || canViewAERecs) && (
                        <div className="DashboardRow">
                            <div style={{ flex: 1 }} />
                            <RecommendationsCalendar propertyId={propertyId} currency={currency} filters={datesRange} />
                            <div style={{ flex: 1 }} />
                        </div>
                    )}
                    <div className="DashboardRow">
                        <Widget title="Business review">
                            {currentStats.fetching ||
                                roomTypes.fetching ||
                                inventoryBlocks.fetching ? (
                                <Spinner />
                            ) : (
                                <BusinessReview
                                    currency={currency}
                                    startDate={startDate}
                                    endDate={endDate}
                                    currentData={currentStats.data}
                                    forecast={forecast.data}
                                    budget={budget.data}
                                    statistics={statistics.data}
                                    sellingRate={sellingRate.data}
                                    defaultRoomType={get(roomTypes, 'data.defaultRoomType', {})}
                                    inventoryBlocks={inventoryBlocks.data}
                                    dayClicked={onClickDayHandler}
                                    options={{
                                        showBlocks: !(
                                            VENDOR_TYPES.HOTSOFT === selectedPropertyVendorId ||
                                            VENDOR_TYPES.HOTSOFT_HOIST_API === selectedPropertyVendorId
                                        ),
                                    }}
                                />
                            )}
                        </Widget>
                    </div>
                    <div className="DashboardRow">
                        <Widget title="Room status overview">
                            {isRoomRevenueChangesLoading ? (
                                <Spinner />
                            ) : (
                                <StatusOverview
                                    type="Rooms"
                                    current={currentStats.data}
                                    lyFinal={lyFinalStats.data}
                                    lySpit={lySpitStats.data}
                                    budget={budget.data}
                                    forecast={forecast.data}
                                    canViewForecast={canViewRoomForecast}
                                    currency={currency}
                                    dayComparison={dayComparison}
                                />
                            )}
                        </Widget>
                        <Widget title="Revenue status overview">
                            {isRoomRevenueChangesLoading ? (
                                <Spinner />
                            ) : (
                                <StatusOverview
                                    type="Revenue"
                                    current={currentStats.data}
                                    lyFinal={lyFinalStats.data}
                                    lySpit={lySpitStats.data}
                                    budget={budget.data}
                                    forecast={forecast.data}
                                    canViewForecast={canViewRevenueForecast}
                                    currency={currency}
                                    dayComparison={dayComparison}
                                />
                            )}
                        </Widget>
                    </div>
                    <div className="DashboardRow">
                        <BusinessAnalysis propertyId={propertyId} currency={currency} filters={datesRange} />
                    </div>
                    <div className="DashboardRow">
                        <BusinessStatus
                            filters={datesRange}
                            currency={currency}
                            propertyId={propertyId}
                            currentStats={currentStats}
                            lyFinalStats={lyFinalStats}
                            lySpitStats={lySpitStats}
                            y2FinalStats={y2FinalStats}
                            y2SpitStats={y2SpitStats}
                            retryFunc={getApiData}
                            dayComparison={dayComparison}
                        />
                    </div>
                    <div className="DashboardRow">
                        {canViewInventoryBlocks ? (
                            <InventoryBlocks propertyId={propertyId} currency={currency} filters={datesRange} />
                        ) : (
                            <div style={{ flex: 1 }} />
                        )}
                        <BusinessChange propertyId={propertyId} currency={currency} />
                    </div>
                    {canViewOTAInsights && (
                        <div className="DashboardRow">
                            <Widget title="Competitor Pricing">
                                {isOtaInsightsLoading ? (
                                    <Spinner />
                                ) : (
                                    <OTAInsights
                                        startDate={startDate}
                                        endDate={endDate}
                                        otaCompsets={otaCompsets || []}
                                        otaHotelId={otaHotelId}
                                        canViewOTACompsets={canViewOTACompsets}
                                        propertyId={propertyId}
                                        {...otaInsights}
                                    />
                                )}
                            </Widget>
                        </div>
                    )}
                    {canViewNotes && (
                        <div className="DashboardRow">
                            <Widget title="Notes">
                                <NotesManagement
                                    start={dayjs(startDate).format('YYYY-MM-DD')}
                                    end={dayjs(endDate).format('YYYY-MM-DD')}
                                />
                            </Widget>
                        </div>
                    )}
                    {canViewEvents && (
                        <div className="DashboardRow">
                            <Widget title="Events">
                                <div style={{ padding: '20px' }}>
                                    <EventsManagement events={events} refreshEvents={fetchEvents} />
                                </div>
                            </Widget>
                        </div>
                    )}
                </Col>
            </Row >
        </>
    );
};

export default Dashboard;
